/**
 * Self-hosted Inter from `inter-ui` (`inter-variable.css`). That face includes the
 * weight axis and the optical-size axis (`opsz`, 14–32), plus Inter's OpenType
 * stylistic sets and character variants. The Fontsource build does not.
 *
 * Change the family, the optical size, or the feature map below to tune Inter.
 */

const SYSTEM_FALLBACK = [
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
] as const;

export const FONT_FAMILY = 'InterVariable';

export function getFontFamilyStack(): string {
  return [FONT_FAMILY, ...SYSTEM_FALLBACK].join(', ');
}

/** `'auto'` follows the rendered font-size. A number pins `opsz` (14–32). */
export const INTER_OPTICAL_SIZE: 'auto' | number = 'auto';

export const INTER_STYLISTIC_FEATURES: Readonly<Record<string, 0 | 1>> = {
  ss03: 1, // round quotes
  cv01: 1, // alternate 1
  cv05: 1, // tailed l
  cv10: 1, // spurred G
  cv06: 1, // simplified u
};

/**
 * Added on top of the stylistic features for all-caps text only. `case` lifts brackets, hyphens and
 * colons to cap height, which looks misplaced next to lowercase letters.
 */
export const INTER_UPPERCASE_FEATURES: Readonly<Record<string, 0 | 1>> = {
  case: 1, // punctuation aligned with capitals
};

export type InterFontStyle = {
  fontOpticalSizing: 'auto' | 'none';
  fontVariationSettings?: string;
  fontFeatureSettings?: string;
};

export function buildInterFontStyle(
  opticalSize: 'auto' | number,
  features: Readonly<Record<string, 0 | 1>>,
): InterFontStyle {
  const fontFeatureSettings = Object.entries(features)
    .map(([tag, enabled]) => `"${tag}" ${enabled}`)
    .join(', ');

  return {
    fontOpticalSizing: opticalSize === 'auto' ? 'auto' : 'none',
    ...(opticalSize === 'auto' ? {} : { fontVariationSettings: `"opsz" ${opticalSize}` }),
    ...(fontFeatureSettings ? { fontFeatureSettings } : {}),
  };
}

/** Inter settings for `<body>`, so every element inherits them. Pass extra features for a variant that needs more. */
export function interFontStyle(extraFeatures: Readonly<Record<string, 0 | 1>> = {}): InterFontStyle {
  return buildInterFontStyle(INTER_OPTICAL_SIZE, { ...INTER_STYLISTIC_FEATURES, ...extraFeatures });
}
